import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, ArrowRight } from "lucide-react";

const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (project) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [project, onClose]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[80] flex items-stretch justify-center"
          data-testid="project-modal"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" aria-hidden="true" />

          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative my-4 md:my-6 mx-3 md:mx-6 w-full max-w-[1280px] bg-[#FBF8F1] border border-black/15 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.55)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="project-modal-panel"
          >
            <div className="sticky top-0 z-10 bg-[#FBF8F1] border-b border-black/10">
              <div className="px-5 md:px-8 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="overline">{project.index}</span>
                  <span className="overline text-black/50">{project.year}</span>
                  <span className="overline text-black/50 hidden md:inline">· {project.role}</span>
                </div>
                <button
                  onClick={onClose}
                  className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] font-semibold hover:text-[var(--accent)] transition-colors"
                  data-testid="project-modal-close"
                >
                  Close
                  <span className="inline-block transition-transform group-hover:rotate-90">
                    <X size={16} />
                  </span>
                </button>
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(100vh-72px)]">
              {/* Above-the-fold: title + tagline (left) and main video (right) — both visible on open */}
              <div className="grid grid-cols-12 gap-5 md:gap-8 px-5 md:px-8 pt-5 md:pt-7 pb-6 md:pb-8">
                <div className="col-span-12 lg:col-span-5 flex flex-col justify-center">
                  <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                    className="editorial-h1 text-3xl md:text-5xl lg:text-6xl mb-3"
                  >
                    {project.title}
                  </motion.h2>
                  {project.tagline && (
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.18, duration: 0.6 }}
                      className="text-base md:text-lg leading-snug text-black/70 mb-4"
                    >
                      {project.tagline}
                    </motion.p>
                  )}
                  <div className="flex flex-wrap gap-3 text-xs uppercase tracking-[0.18em] font-semibold">
                    <span className="border border-black/20 px-2.5 py-1">{project.year}</span>
                    <span className="border border-black/20 px-2.5 py-1">{project.role}</span>
                  </div>
                </div>

                <div className="col-span-12 lg:col-span-7">
                  {project.videos?.length > 0 ? (
                    <motion.figure
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.15, duration: 0.6 }}
                      className="group relative"
                    >
                      <div className="aspect-video bg-black overflow-hidden">
                        <video
                          src={project.videos[0].src}
                          poster={project.image}
                          controls
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                          data-testid="project-modal-video-0"
                        />
                      </div>
                    </motion.figure>
                  ) : (
                    <motion.img
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.15, duration: 0.6 }}
                      src={project.image}
                      alt={project.title}
                      className="w-full aspect-video object-cover"
                      data-testid="project-modal-image"
                    />
                  )}
                </div>
              </div>

              {/* Below the fold — overview, highlights, quote, gallery */}
              <div className="px-5 md:px-8 pb-10 md:pb-14">
                <div className="grid grid-cols-12 gap-6 mb-10">
                  <div className="col-span-12 md:col-span-7">
                    <div className="overline mb-3">— Overview</div>
                    <p className="text-base md:text-lg leading-relaxed">{project.summary}</p>
                  </div>
                  {project.details?.length > 0 && (
                    <div className="col-span-12 md:col-span-5">
                      <div className="overline mb-3">— Highlights</div>
                      <ul className="space-y-2.5 text-base">
                        {project.details.map((d, i) => (
                          <li
                            key={i}
                            className="group flex gap-3 border-b border-black/10 pb-2.5 hover:border-[var(--accent)] transition-colors"
                          >
                            <span className="text-[var(--accent)] flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1">
                              <ArrowRight size={16} />
                            </span>
                            <span className="leading-snug">{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {project.pullQuote && (
                  <blockquote
                    className="my-10 border-l-2 border-[var(--accent)] pl-5 md:pl-8 max-w-4xl"
                    data-testid={`project-modal-quote-${project.id}`}
                  >
                    <p className="text-xl md:text-2xl leading-tight font-medium mb-3">
                      “{project.pullQuote.text}”
                    </p>
                    <footer className="overline text-black/65">— {project.pullQuote.author}</footer>
                  </blockquote>
                )}

                {(project.gallery?.length > 0 || project.galleryVideos?.length > 0) && (
                  <div className="mt-2">
                    <div className="overline mb-4">— Gallery</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {project.galleryVideos?.map((v, i) => (
                        <figure key={`gv-${i}`} className="md:col-span-2 lg:col-span-3 group">
                          <div className="relative aspect-video bg-black overflow-hidden">
                            <video
                              src={v.src}
                              poster={project.image}
                              controls
                              playsInline
                              preload="metadata"
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                              data-testid={`project-modal-gallery-video-${i}`}
                            />
                            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[var(--ink)] text-[var(--bg)] px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold opacity-90 pointer-events-none">
                              <Play size={11} /> Video
                            </div>
                          </div>
                          {v.title && (
                            <figcaption className="overline mt-2 text-black/55">{v.title}</figcaption>
                          )}
                        </figure>
                      ))}
                      {project.gallery?.map((g, i) => (
                        <figure key={`g-${i}`} className="group overflow-hidden bg-black/5">
                          <img
                            src={g}
                            alt=""
                            loading="lazy"
                            className="w-full h-full object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-110"
                            data-testid={`project-modal-gallery-${i}`}
                          />
                        </figure>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
